import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Paper, Fab, Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete, Grid, Card, CardContent, Avatar, Typography, CircularProgress, IconButton, Tabs, Tab, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useMediaQuery } from '@mui/material';
import { useSwipeable } from 'react-swipeable';
import PhoneIcon from '@mui/icons-material/Phone';
import TextsmsIcon from '@mui/icons-material/Textsms';

// Fix for marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return 'N/A';

  // Remove all non-numeric characters
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');

  // Check if the input is of correct length
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phoneNumber; // Return the original input if it doesn't match the expected format
};

const PhoneBook = () => {
  const [orderBy, setOrderBy] = useState('unitNumber');
  const [order, setOrder] = useState('asc');
  const [responders, setResponders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedResponder, setSelectedResponder] = useState(null);
  const [newResponder, setNewResponder] = useState({
    UnitNumber: '',
    Name: '',
    Email: '',
    Phone: '',
    Address: '',
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortColumn, setSortColumn] = useState('UnitNumber');
  const [importantAddresses, setImportantAddresses] = useState([]);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressSearchTerm, setAddressSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [error, setError] = useState(null);
  const [swipedResponderId, setSwipedResponderId] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [activeTab, setActiveTab] = useState('responders');

  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    const fetchResponders = async () => {
      try {
        setLoading(true);
        const respondersSnapshot = await getDocs(collection(db, 'users'));
        const respondersList = respondersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log('Fetched Responders:', respondersList); // Debugging log
        setResponders(respondersList);
      } catch (error) {
        console.error('Error fetching responders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResponders();
  }, []);

  useEffect(() => {
    const fetchImportantAddresses = async () => {
      try {
        setLoadingAddresses(true);
        const addressesSnapshot = await getDocs(collection(db, 'hvac', 'GenericData', 'ImportantAddresses'));
        const addressesList = addressesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setImportantAddresses(addressesList);
      } catch (error) {
        console.error('Error fetching important addresses:', error);
        setError('Failed to fetch addresses.');
      } finally {
        setLoadingAddresses(false);
      }
    };

    fetchImportantAddresses();
  }, []);

  useEffect(() => {
    console.log("Responders:", responders); // Log the responders to inspect their structure
  }, [responders]);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoadingDoctors(true);
        const doctorsSnapshot = await getDocs(collection(db, 'hvac', 'GenericData', 'Doctors'));
        const doctorsList = doctorsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDoctors(doctorsList);

        // Extract unique specialties
        const uniqueSpecialties = [...new Set(doctorsList.map(doctor => doctor.Specialty))];
        setSpecialties(uniqueSpecialties);
      } catch (error) {
        console.error('Error fetching doctors:', error);
      } finally {
        setLoadingDoctors(false);
      }
    };

    fetchDoctors();
  }, []);

  const filteredResponders = responders.filter(responder => {
    const displayName = responder.displayName ? responder.displayName.toLowerCase().includes(searchTerm.toLowerCase()) : false;
    const unitNumber = responder.unitNumber ? responder.unitNumber.toLowerCase().includes(searchTerm.toLowerCase()) : false;
    const phone = responder.cellPhone ? responder.Phone.toLowerCase().includes(searchTerm.toLowerCase()) : false;

    return displayName || unitNumber || phone;
  });

  const filteredDoctors = selectedSpecialty
    ? doctors.filter(doctor => doctor.Specialty === selectedSpecialty)
    : doctors;

  const sortResponders = (column) => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    const sortedResponders = [...responders].sort((a, b) => {
      const aValue = a[column] || ''; // Default to empty string if undefined
      const bValue = b[column] || ''; // Default to empty string if undefined
      return isAsc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
    setResponders(sortedResponders);
  };

  const handleOpenAddDialog = () => {
    setNewResponder({
      UnitNumber: '',
      Name: '',
      Email: '',
      Phone: '',
      Address: '',
    });
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setAddressSuggestions([]);
  };

  const handleOpenEditDialog = (responder) => {
    setSelectedResponder(responder);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedResponder(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = name === 'cellNumber' ? formatPhoneNumber(value) : value; // Format phone number if it's the phone field
    setNewResponder(prevState => ({
      ...prevState,
      [name]: formattedValue,
    }));
  };

  const handleImageChange = (e) => {
    // Removed image handling logic
  };

  const handleAddResponder = async () => {
    try {
      await addDoc(collection(db, 'users'), {
        ...newResponder,
      });
      handleCloseAddDialog();
      const respondersSnapshot = await getDocs(collection(db, 'users'));
      const respondersList = respondersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setResponders(respondersList);
    } catch (error) {
      console.error('Error adding responder:', error);
    }
  };

  const handleEditResponder = async () => {
    try {
      const responderRef = doc(db, 'users', selectedResponder.id);
      await updateDoc(responderRef, {
        ...newResponder,
      });
      handleCloseEditDialog();
      const respondersSnapshot = await getDocs(collection(db, 'users'));
      const respondersList = respondersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setResponders(respondersList);
    } catch (error) {
      console.error('Error editing responder:', error);
    }
  };

  const fetchAddressSuggestions = async (query) => {
    if (query) {
      try {
        const response = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&addressdetails=1&limit=10`);
        setAddressSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const debouncedFetchAddressSuggestions = debounce(fetchAddressSuggestions, 300);

  const handleAddressChange = (value) => {
    setNewResponder(prevState => ({
      ...prevState,
      Address: value ? value.label : '',
    }));

    if (value) {
      debouncedFetchAddressSuggestions(value.label);
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleOpenAddressDialog = (address) => {
    setSelectedAddress(address);
    setOpenAddressDialog(true);
  };

  const handleCloseAddressDialog = () => {
    setOpenAddressDialog(false);
    setSelectedAddress(null);
  };

  const handleAddBuilding = () => {
    // Logic to add a new building will go here
  };

  // Filtered addresses based on search term
  const filteredAddresses = importantAddresses.filter(address => {
    const addressLine = address.Address ? address.Address.toLowerCase().includes(addressSearchTerm.toLowerCase()) : false;
    const buildingName = address.BuildingName ? address.BuildingName.toLowerCase().includes(addressSearchTerm.toLowerCase()) : false;

    return addressLine || buildingName;
  });

  // Example function to check if the user is an admin
  const checkAdminStatus = () => {
    // Logic to determine if the user is an admin
    // This is just a placeholder; implement your actual logic here
    setIsAdmin(true); // Set to true for demonstration
  };

  // Call the function to check admin status when the component mounts
  useEffect(() => {
    checkAdminStatus();
  }, []);

  // Sort responders based on the selected column and direction
  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    const sortedResponders = [...responders].sort((a, b) => {
      if (a[column] < b[column]) return isAsc ? 1 : -1;
      if (a[column] > b[column]) return isAsc ? -1 : 1;
      return 0;
    });
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
    setResponders(sortedResponders);
  };

  // Sort responders by Unit Number ascending by default
  useEffect(() => {
    if (responders.length > 0) {
      const sortedResponders = [...responders].sort((a, b) => {
        const aUnitNumber = a.unitNumber || ''; // Default to empty string if undefined
        const bUnitNumber = b.unitNumber || ''; // Default to empty string if undefined
        return aUnitNumber.localeCompare(bUnitNumber);
      });
      setResponders(sortedResponders);
    }
  }, [responders.length]);

  const handleSwipe = (responderId) => {
    setSwipedResponderId(responderId);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ flexGrow: 1, padding: '20px', textAlign: 'center' }}>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12}>
          <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
            <Tab label="Responders" value="responders" />
            <Tab label="Important Addresses" value="addresses" />
            <Tab label="Doctors" value="doctors" />
          </Tabs>
        </Grid>

        <Grid item xs={12} md={8}>
          {activeTab === 'responders' && (
            <Card variant="outlined" sx={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)', borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>Phone Book</Typography>
                <TextField
                  label="Search"
                  variant="outlined"
                  fullWidth
                  onChange={(e) => setSearchTerm(e.target.value)}
                  value={searchTerm}
                  sx={{ marginBottom: '16px' }}
                />
                {loading ? (
                  <CircularProgress />
                ) : (
                  <TableContainer component={Paper} sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={sortColumn === 'unitNumber'}
                              direction={sortDirection}
                              onClick={() => sortResponders('unitNumber')}
                            >
                              Unit Number
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={sortColumn === 'displayName'}
                              direction={sortColumn === 'displayName' ? sortDirection : 'asc'}
                              onClick={() => sortResponders('displayName')}
                            >
                              Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={sortColumn === 'Phone'}
                              direction={sortColumn === 'Phone' ? sortDirection : 'asc'}
                              onClick={() => sortResponders('Phone')}
                            >
                              Phone
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredResponders.length > 0 ? (
                          filteredResponders.map((responder) => (
                            <SwipeableResponder
                              key={responder.id}
                              responder={responder}
                              swipedResponderId={swipedResponderId}
                              handleSwipe={handleSwipe}
                            />
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">No responders found.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'addresses' && (
            <Card variant="outlined" sx={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)', borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>Important Addresses</Typography>
                {loadingAddresses ? (
                  <CircularProgress />
                ) : error ? (
                  <Typography color="error">{error}</Typography>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Address</TableCell>
                          <TableCell>Access Code</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importantAddresses.map((address) => (
                          <TableRow key={address.id}>
                            <TableCell>{address.addressLine1}</TableCell>
                            <TableCell>{address.accessCode}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'doctors' && (
            <Card variant="outlined" sx={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)', borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>Doctors</Typography>
                <Autocomplete
                  options={specialties}
                  getOptionLabel={(option) => option}
                  onChange={(event, newValue) => setSelectedSpecialty(newValue)}
                  renderInput={(params) => <TextField {...params} label="Select Specialty" variant="outlined" />}
                  fullWidth
                  sx={{ marginBottom: '16px' }}
                />
                {loadingDoctors ? (
                  <CircularProgress />
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Specialty</TableCell>
                          <TableCell>Phone</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredDoctors.length > 0 ? (
                          filteredDoctors.map((doctor) => (
                            <TableRow key={doctor.id}>
                              <TableCell>{doctor.Name}</TableCell>
                              <TableCell>{doctor.Specialty}</TableCell>
                              <TableCell>
                                <a href={`tel:${doctor.Phone}`}>
                                  {formatPhoneNumber(doctor.Phone)}
                                </a>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">No doctors found.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const SwipeableResponder = ({ responder, swipedResponderId, handleSwipe }) => {
  const [isSwiped, setIsSwiped] = useState(false);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      setIsSwiped(true);
      handleSwipe(responder.id);
    },
    onSwipedRight: () => {
      setIsSwiped(false);
      handleSwipe(null);
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const handleClickOutside = () => {
    if (isSwiped) {
      setIsSwiped(false);
      handleSwipe(null);
    }
  };

  return (
    <TableRow
      {...handlers}
      onClick={handleClickOutside}
      style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer', transition: 'background-color 0.3s', '&:hover': { backgroundColor: '#f5f5f5' } }}
    >
      <TableCell>{responder.unitNumber}</TableCell>
      <TableCell>{responder.displayName.substring(4)}</TableCell>
      <TableCell>{formatPhoneNumber(responder.cellNumber)}</TableCell>
      {swipedResponderId === responder.id && (
        <div
          id={`actions-${responder.id}`}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f0f0f0',
            padding: '10px',
            transform: isSwiped ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.3s ease',
          }}
        >
          <IconButton href={`tel:${responder.cellNumber}`} style={{ marginRight: '10px' }}>
            <PhoneIcon color="primary" />
          </IconButton>
          <IconButton href={`sms:${responder.cellNumber}`}>
            <TextsmsIcon color="primary" />
          </IconButton>
        </div>
      )}
    </TableRow>
  );
};

export default PhoneBook;
